import {useState} from 'react'
import {Link} from 'react-router-dom'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {api, ApiError} from '../../../services/api'
import {useToast} from '../../../components/Toast'

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
})

export function ForgotPassword() {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)

  const formik = useFormik({
    initialValues: {email: ''},
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        await api.forgotPassword(values.email)
        setSent(true)
        toast.success('If that email is registered, a reset link is on its way.')
      } catch (error) {
        toast.error(error instanceof ApiError ? error.message : 'Something went wrong. Please try again.')
      } finally {
        setLoading(false)
      }
    },
  })

  return (
    <div
      style={{
        background: '#ffffff',
        borderRadius: '16px',
        padding: 'clamp(28px, 7vw, 48px) clamp(20px, 6vw, 40px)',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 14px 44px rgba(27,27,41,0.1)',
        border: '1px solid #eef0f4',
      }}
    >
      <div style={{textAlign: 'center', marginBottom: '32px'}}>
        <img
          src='/app-icon.png'
          alt='App Icon'
          style={{
            width: '72px',
            height: '72px',
            borderRadius: '16px',
            marginBottom: '20px',
            boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
            objectFit: 'cover',
          }}
        />
        <h1 style={{fontSize: '24px', fontWeight: 700, color: '#1e293b', margin: '0 0 6px'}}>
          Forgot password?
        </h1>
        <p style={{fontSize: '14px', color: '#64748b', margin: 0}}>
          {sent
            ? 'Check your inbox for the reset link.'
            : 'Enter your email and we’ll send you a reset link.'}
        </p>
      </div>

      {sent ? (
        <>
          <div
            style={{
              background: '#f0faf4',
              border: '1px solid #bbe6cd',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
              color: '#027a34',
              fontSize: '14px',
              lineHeight: 1.5,
            }}
          >
            If an account exists for <strong>{formik.values.email}</strong>, you’ll receive an email with
            a link to reset your password. The link expires in 1 hour.
          </div>
          <Link
            to='/auth/login'
            style={{
              display: 'block',
              textAlign: 'center',
              padding: '13px',
              background: 'linear-gradient(135deg, #039540, #027a34)',
              color: '#ffffff',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 600,
              textDecoration: 'none',
              letterSpacing: '0.3px',
            }}
          >
            Back to sign in
          </Link>
        </>
      ) : (
        <form onSubmit={formik.handleSubmit} noValidate>
          <div style={{marginBottom: '20px'}}>
            <label style={{display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px'}}>
              Email Address
            </label>
            <div style={{position: 'relative'}}>
              <div
                style={{
                  position: 'absolute',
                  left: '14px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                }}
              >
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                  <path d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z' />
                  <polyline points='22,6 12,13 2,6' />
                </svg>
              </div>
              <input
                type='email'
                placeholder='you@example.com'
                autoComplete='off'
                {...formik.getFieldProps('email')}
                style={{
                  width: '100%',
                  padding: '12px 14px 12px 44px',
                  border: `1px solid ${formik.touched.email && formik.errors.email ? '#f87171' : '#e5e7eb'}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#1e293b',
                  outline: 'none',
                  boxSizing: 'border-box',
                  background: '#f9fafb',
                }}
              />
            </div>
            {formik.touched.email && formik.errors.email && (
              <p style={{color: '#dc2626', fontSize: '12px', margin: '4px 0 0'}}>{formik.errors.email}</p>
            )}
          </div>

          <button
            type='submit'
            disabled={loading}
            style={{
              width: '100%',
              padding: '13px',
              background: loading ? '#7ec79a' : 'linear-gradient(135deg, #039540, #027a34)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'opacity 0.2s',
              letterSpacing: '0.3px',
            }}
          >
            {loading ? (
              <span style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}>
                <span
                  style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255,255,255,0.4)',
                    borderTopColor: '#ffffff',
                    borderRadius: '50%',
                    display: 'inline-block',
                    animation: 'spin 0.8s linear infinite',
                  }}
                />
                Sending...
              </span>
            ) : (
              'Send reset link'
            )}
          </button>

          <p style={{textAlign: 'center', fontSize: '14px', color: '#64748b', margin: '22px 0 0'}}>
            Remember your password?{' '}
            <Link to='/auth/login' style={{color: '#039540', fontWeight: 600, textDecoration: 'none'}}>
              Sign in
            </Link>
          </p>
        </form>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
